import { __ } from "@wordpress/i18n";
import { createContext, useContext, useEffect, useState } from "@wordpress/element";
import { v4 as uuidv4 } from "uuid";
import { DEFAULTS, LS } from "../../utils/constants";
import { GPT_LANGUAGES } from "../../utils/language";

export type OutlineObjectType = {
    id?: string;
    title: string;
    url?: string;
    slug?: string;
    loading?: boolean;
    post_id?: number;
    caretOffset?: number;
};

export type DocOutlineType = OutlineObjectType & {
    lessons?: (OutlineObjectType & {
        sub_lessons?: OutlineObjectType[];
    })[];
};

export type DocPreferencesType = {
    lessonsCountAuto?: boolean;
    lessonsCount?: number;
    subLessonsCount?: number;
    language?: string;
    tone?: string;
    modifiedTone?: string;
    subject?: string;
    slugLang?: string;
    slugLength?: number;
    slugLengthAuto?: boolean;
    status?: string;
    files?: File[];
};

export type DocState = {
    outline: DocOutlineType | null;
    setOutline: (outline: DocOutlineType | null) => void;
    focusedLessonId?: string;
    setFocusedLessonId: (id: string) => void;
    loading: boolean;
    setLoading: (loading: boolean) => void;
    error: string;
    setError: (error: string) => void;
    prompt: string;
    setPrompt: (prompt: string) => void;
    preferences: DocPreferencesType | null;
    setPreferences: (preferences: DocPreferencesType | null) => void;
    addLessonAfter: (currentLessonId?: string, title?: string, lessonType?: "lesson" | "sub_lesson") => void;
    removeLesson: (currentLessonId?: string) => void;
    moveLessonFocus: (currentLessonId?: string, direction?: "next" | "prev") => void;
};

const DocContext = createContext<DocState | undefined>(undefined);

export function useDoc() {
    const ctx = useContext(DocContext);
    if (!ctx) throw new Error(__("useDoc must be used within a DocProvider", "antimanual"));
    return ctx;
}

export function DocProvider({ children }: { children: React.ReactNode }) {
    const [outline, setOutline] = useState<DocOutlineType | null>(null);
    const [focusedLessonId, setFocusedLessonId] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [prompt, setPrompt] = useState("");
    const [preferences, setPreferences] = useState<DocPreferencesType>({
        lessonsCountAuto: true,
        language: DEFAULTS.LANGUAGE,
        tone: DEFAULTS.TONE,
        slugLang: DEFAULTS.LANGUAGE,
        slugLengthAuto: true,
        status: DEFAULTS.POST_STATUS,
        files: [],
    });

    const handleSetOutline = (outline: DocOutlineType | null) => {
        if (outline) {
            if (!outline.id) {
                outline.id = uuidv4();
            }

            outline.lessons?.map((lesson) => {
                lesson.sub_lessons?.map((subLesson) => {
                    if (!subLesson.id) {
                        subLesson.id = uuidv4();
                    }
                });
                if (!lesson.id) {
                    lesson.id = uuidv4();
                }
            });
        }

        localStorage.setItem(LS.DOC_OUTLINE, JSON.stringify(outline));
        setOutline(outline);
    };

    const handleSetPrompt = (prompt: string) => {
        localStorage.setItem(LS.DOC_PROMPT, prompt);
        setPrompt(prompt);
    };

    const handleSetPreferences = (prefs: DocPreferencesType | null) => {
        if (!prefs) return;

        const newPreferences = { ...preferences };

        if (prefs.lessonsCountAuto !== undefined) {
            newPreferences.lessonsCountAuto = prefs.lessonsCountAuto;
        }

        if (prefs.lessonsCount !== undefined) {
            newPreferences.lessonsCount = prefs.lessonsCount;
        }

        if (prefs.subLessonsCount !== undefined) {
            newPreferences.subLessonsCount = prefs.subLessonsCount;
        }

        if (prefs.tone !== undefined) {
            newPreferences.tone = prefs.tone;
        }

        if (prefs.modifiedTone !== undefined) {
            newPreferences.modifiedTone = prefs.modifiedTone;
        }

        if (prefs.subject !== undefined) {
            newPreferences.subject = prefs.subject;
        }

        if (prefs.slugLang !== undefined) {
            newPreferences.slugLang = prefs.slugLang;
        }

        if (prefs.slugLengthAuto !== undefined) {
            newPreferences.slugLengthAuto = prefs.slugLengthAuto;
        }

        if (prefs.slugLength !== undefined) {
            newPreferences.slugLength = prefs.slugLength;
        }

        if (prefs.status !== undefined) {
            newPreferences.status = prefs.status;
        }

        if (GPT_LANGUAGES.some((lang) => lang.value === prefs.language)) {
            newPreferences.language = prefs.language;
        }

        if (prefs.files !== undefined && prefs.files.every((file) => file instanceof File)) {
            newPreferences.files = prefs.files;
        }

        localStorage.setItem(LS.DOC_PREFERENCES, JSON.stringify(newPreferences));

        setPreferences(newPreferences);
    };

    const addLessonAfter = (currentLessonId?: string, title = "", lessonType: "lesson" | "sub_lesson" = "sub_lesson") => {
        if (!outline?.lessons) return;

        const newLesson = { id: "", title };

        for (let i = 0; i < outline.lessons.length && !newLesson.id; i++) {
            const lesson = outline.lessons[i];

            if (!lesson.sub_lessons) lesson.sub_lessons = [];

            if (lesson.id === currentLessonId) {
                newLesson.id = uuidv4();
                if (lessonType === "lesson") outline.lessons.splice(i + 1, 0, { ...newLesson, sub_lessons: [] });
                else if (lessonType === "sub_lesson") lesson.sub_lessons.unshift(newLesson);
                break;
            }

            if (lessonType !== "sub_lesson") continue;

            for (let j = 0; j < lesson.sub_lessons.length && !newLesson.id; j++) {
                const subLesson = lesson.sub_lessons[j];
                if (subLesson.id === currentLessonId) {
                    newLesson.id = uuidv4();
                    lesson.sub_lessons.splice(j + 1, 0, newLesson);
                }
            }
        }

        setFocusedLessonId((prev) => newLesson.id || prev);
        handleSetOutline({ ...outline });
    };

    const removeLesson = (currentLessonId?: string) => {
        if (!currentLessonId || !outline?.lessons) return;
        let prevLesson: OutlineObjectType | null = null;
        let end = false;

        for (let i = 0; i < outline.lessons.length && !end; i++) {
            const lesson = outline.lessons[i];

            if (lesson.id === currentLessonId) {
                outline.lessons.splice(i, 1);
                break;
            }

            prevLesson = lesson;

            if (lesson.sub_lessons) {
                for (let j = 0; j < lesson.sub_lessons.length && !end; j++) {
                    const subLesson = lesson.sub_lessons[j];

                    if (subLesson.id === currentLessonId) {
                        lesson.sub_lessons.splice(j, 1);
                        end = true;
                        break;
                    }

                    prevLesson = subLesson;
                }
            }
        }

        if (prevLesson?.id) {
            setFocusedLessonId(prevLesson.id);
            prevLesson.caretOffset = prevLesson.title.length;
        }

        handleSetOutline({ ...outline });
    };

    const moveLessonFocus = (currentLessonId?: string, direction: "next" | "prev" = "prev") => {
        if (!currentLessonId || !outline?.lessons) return;

        const flatList: OutlineObjectType[] = [outline];

        for (const lesson of outline.lessons) {
            flatList.push(lesson);
            if (lesson.sub_lessons?.length) {
                flatList.push(...lesson.sub_lessons);
            }
        }

        const currentIndex = flatList.findIndex((item) => item.id === currentLessonId);
        if (currentIndex === -1) return;

        const targetIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

        if (targetIndex < 0 || targetIndex >= flatList.length) return;

        const currentLesson = flatList[currentIndex];
        const targetedLesson = flatList[targetIndex];

        setFocusedLessonId(targetedLesson.id);
        targetedLesson.caretOffset = currentLesson.caretOffset ?? targetedLesson.title.length;

        handleSetOutline({ ...outline });
    };

    useEffect(() => {
        const storedOutline = localStorage.getItem(LS.DOC_OUTLINE);
        if (storedOutline) {
            try {
                const outline: DocOutlineType = JSON.parse(storedOutline);
                outline.loading = false;
                outline.lessons?.forEach((lesson) => {
                    lesson.loading = false;
                    lesson.sub_lessons?.forEach((subLesson) => {
                        subLesson.loading = false;
                    });
                });
                handleSetOutline(outline);
            } catch {}
        }

        const storedPrompt = localStorage.getItem(LS.DOC_PROMPT);
        if (storedPrompt) {
            try {
                handleSetPrompt(storedPrompt);
            } catch {}
        }

        const storedPreferences = localStorage.getItem(LS.DOC_PREFERENCES);
        if (storedPreferences) {
            try {
                handleSetPreferences(JSON.parse(storedPreferences));
            } catch {}
        }
    }, []);

    return (
        <DocContext.Provider
            value={{
                loading,
                setLoading,
                error,
                setError,
                prompt,
                setPrompt: handleSetPrompt,
                outline,
                setOutline: handleSetOutline,
                focusedLessonId,
                setFocusedLessonId,
                preferences,
                setPreferences: handleSetPreferences,
                addLessonAfter,
                removeLesson,
                moveLessonFocus,
            }}
        >
            {children}
        </DocContext.Provider>
    );
}
